import { ColGroupDef, Column, IHeaderGroupParams, IProvidedColumn } from '@ag-grid-community/core';
import dayjs from 'dayjs';

import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { MONTH_ADJUSTMENT_TOOLTIP } from '@shared/store/workflow/workflow.const';
import { AuxExcelStyleKeys } from '@shared/utils/aux-excel-styles/aux-excel-style-keys';

import { QCOpenMonthHeaderGroupComponent } from '../qc-open-month-header-group.component';
import {
  QuarterCloseGridConfigContext,
  QuarterCloseMonthColumnConfig,
} from './qc-grid-config-context';
import { qcMonthColumnGroup } from './qc-month-column-group';
import { FLUX_MONTH_ANALYSIS_FIELDS, QuarterCloseService } from '../../quarter-close.service';

export const qcCurrentMonthColumnGroup = (
  ctx: QuarterCloseGridConfigContext,
  tmp: QuarterCloseMonthColumnConfig,
  quarterCloseService: QuarterCloseService,
  isFluxAnalysisAvailableAndOn: boolean
): ColGroupDef => {
  const monthColumn = qcMonthColumnGroup(ctx, tmp, isFluxAnalysisAvailableAndOn);

  return {
    ...monthColumn,
    headerClass: [
      'text-center',
      'font-semibold',
      'header-marker',
      'green-light-marker',
      tmp.index === 1 ? AuxExcelStyleKeys.ALTERNATE : '',
    ],
    headerGroupComponent: QCOpenMonthHeaderGroupComponent,
    headerGroupComponentParams: {
      collapsedByDefault: !tmp.isCurrent,
      disabled:
        !ctx.isCloseMonthAvailable || !ctx.permissions.canCloseMonth || ctx.isCloseMonthsProcessing,
      expandableCols: [
        `qmonth${tmp.index}_monthly_wp_estimate`,
        `qmonth${tmp.index}_monthly_invoices_received`,
        `qmonth${tmp.index}_net_change_in_accruals`,
        `qmonth${tmp.index}_wp_ltd`,
        `qmonth${tmp.index}_invoiced_ltd`,
        `qmonth${tmp.index}_unpaid_invoices_ltd`,
        `qmonth${tmp.index}_advanced_payments_ltd`,
        `qmonth${tmp.index}_net_position`,
      ],
      filterCols: (column: IProvidedColumn) => {
        const id = column.getId();
        const fluxAnalysisAvailableAndOn =
          localStorage.getItem(LocalStorageKey.FLUX_ANALYSIS_MODE) === 'true' &&
          quarterCloseService.isFluxAnalysisAvailable();

        if (!fluxAnalysisAvailableAndOn) {
          if (FLUX_MONTH_ANALYSIS_FIELDS.includes(id)) {
            return false;
          }
        } else {
          if (
            [
              `qmonth${tmp.index}_advanced_payments_ltd`,
              `qmonth${tmp.index}_net_change_in_accruals`,
              `qmonth${tmp.index}_net_position`,
              `qmonth${tmp.index}_unpaid_invoices_ltd`,
            ].includes(id)
          ) {
            return false;
          }
        }

        return (
          !/_accrual_debit|_accrual_credit|_prepaid_debit|_prepaid_credit/.test(id) &&
          ![`qmonth${tmp.index}_accrual_ltd`, `qmonth${tmp.index}_prepaid_ltd`].includes(id)
        );
      },
      localStorageKey: LocalStorageKey.CLOSE_SUMMARY_OPEN_MONTH_GROUP,
      monthButtonClick: ctx.onMonthCloseClicked,
      onToggle: (params: IHeaderGroupParams) => {
        params.api.refreshCells({
          columns: (params.columnGroup.getDisplayedChildren() || []) as Column[],
          force: true,
        });
      },
      tooltip: getMonthCloseTooltip(ctx),
      // onToggle: (params: IHeaderGroupParams) => {
      //   const adjustedColumns = (
      //     params.columnGroup.getDisplayedChildren() as Column[]
      //   )?.filter((children) => /_accrual_ltd|_prepaid_ltd/.test(children.getColId()));
      //
      //   params.api.getAllDisplayedColumns().forEach((column) => {
      //     if (
      //       adjustedColumns?.some(
      //         (adjColumn) => column.getColId() === adjColumn.getUniqueId()
      //       )
      //     ) {
      //       const adjWidth = column.getActualWidth() === 150 ? 170 : 150;
      //       column.setActualWidth(adjWidth);
      //     }
      //   });
      //   setTimeout(() => {
      //     // params.api.refreshHeader();
      //     // params.api.refreshCells({
      //     //   columns: adjustedColumns,
      //     //   force: true,
      //     // });
      //     // params.api.redrawRows();
      //   }, 0);
      // },
    },
    openByDefault: true,
  };
};

const getMonthCloseTooltip = (ctx: QuarterCloseGridConfigContext): string => {
  if (!ctx.isWorkflowAvailable) {
    const d = dayjs(ctx.openMonth);

    return `${d.format('MMMM YYYY')} is not available to close`;
  }

  if (!ctx.isCloseMonthAvailable) {
    return MONTH_ADJUSTMENT_TOOLTIP;
  }

  if (!ctx.permissions.canCloseMonth) {
    return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  }

  return '';
};
